import { Injectable } from '@angular/core';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { FormControlsOf } from '@y42/angular/forms';
import {
  ColumnTestForm,
  DataTest,
  DataTestType,
  LowCodeTestForm,
  ReferentialIntegrityTestForm,
  RowDriftTestForm,
  SchemaTestForm,
  UniqueTestForm,
} from '@y42/data-test';
import { ColumnTypeEnum, OperationModel } from '@y42/json-schema';
import { UnwrapArray } from '@y42/typescript';
import { isNullish } from '@y42/util/nullish';
import { integerValidator } from '../helpers/integer.validator';
import { minNotIncludingValidator } from '../helpers/min-not-including.validator';

type ColumnTestFormObject = Required<ColumnTestForm['column_conditions']> & Pick<ColumnTestForm, 'column'>;
export interface SchemaFormGroup {
  allowNewColumns: FormControl<boolean>;
  schema: FormArray<FormGroup<SchemaTestSchemaFormGroup>>;
}
export type SchemaTestSchemaFormGroup = FormControlsOf<Required<UnwrapArray<SchemaTestForm['schema']>>>;
export type RowDriftTestFormGroup = FormControlsOf<Required<RowDriftTestForm['row_change']>>;
export type UniqueFormGroup = FormControlsOf<Required<Omit<UniqueTestForm, 'type'>>>;
export type ReferentialIntegrityFormGroup = FormControlsOf<Required<Omit<ReferentialIntegrityTestForm, 'type'>>>;
export type ColumnTestFormGroup = FormControlsOf<ColumnTestFormObject>;
export type LowCodeFormGroup = FormControlsOf<Required<Omit<LowCodeTestForm, 'type'>>>;

interface MainFormGroup {
  dataTestType: FormControl<DataTestType>;
  [DataTestType.Schema]: FormGroup<SchemaFormGroup>;
  [DataTestType.Column]: FormGroup<ColumnTestFormGroup>;
  [DataTestType.Unique]: FormGroup<UniqueFormGroup>;
  [DataTestType.LowCode]: FormGroup<LowCodeFormGroup>;
  [DataTestType.ReferentialIntegrity]: FormGroup<ReferentialIntegrityFormGroup>;
  [DataTestType.NumRowDrift]: FormGroup<RowDriftTestFormGroup>;
}

interface DataTestBuilderFormDefaultValues {
  lowCodeTestRowsFailedByCondition: number;
  lowCodeTestRowsFailedCondition: NonNullable<LowCodeTestForm['rows_failed_condition']>;
  columnTestRowsFailedByCondition: number;
  columnTestRowsFailedCondition: NonNullable<ColumnTestForm['column_conditions']['rows_failed_condition']>;
  referentialIntegrityTestRowsFailedByCondition: number;
  referentialIntegrityTestRowsFailedCondition: NonNullable<ReferentialIntegrityTestForm['rows_failed_condition']>;
}

@UntilDestroy()
@Injectable()
export class DataTestBuilderFormService {
  constructor() {
    this.assignValidators();
  }

  private readonly DEFAULT_VALUES: DataTestBuilderFormDefaultValues = {
    columnTestRowsFailedCondition: 'absolute',
    columnTestRowsFailedByCondition: 0,
    lowCodeTestRowsFailedCondition: 'absolute',
    lowCodeTestRowsFailedByCondition: 0,
    referentialIntegrityTestRowsFailedByCondition: 0,
    referentialIntegrityTestRowsFailedCondition: 'absolute',
  };

  private readonly mainFormGroup: MainFormGroup = {
    dataTestType: this.createDataTestTypeFormControl(),
    [DataTestType.Schema]: this.createSchemaForm(),
    [DataTestType.Column]: this.createColumnTestForm(),
    [DataTestType.LowCode]: this.createLowCodeTestForm(),
    [DataTestType.ReferentialIntegrity]: this.createReferentialIntegrityForm(),
    [DataTestType.NumRowDrift]: this.createNumRowDriftForm(),
    [DataTestType.Unique]: this.createUniqueForm(),
  };

  private readonly resetMainFormGroupByKey: Record<keyof MainFormGroup, () => void> = {
    dataTestType: () => this.mainFormGroup.dataTestType.reset(),
    [DataTestType.Schema]: () => this.mainFormGroup[DataTestType.Schema].reset(),
    [DataTestType.Column]: () => this.mainFormGroup[DataTestType.Column].reset(),
    [DataTestType.LowCode]: () => this.mainFormGroup[DataTestType.LowCode].reset(),
    [DataTestType.ReferentialIntegrity]: () => this.mainFormGroup[DataTestType.ReferentialIntegrity].reset(),
    [DataTestType.NumRowDrift]: () => this.mainFormGroup[DataTestType.NumRowDrift].reset(),
    [DataTestType.Unique]: () => this.mainFormGroup[DataTestType.Unique].reset(),
  };

  private readonly dataTestToSaveFunctionByType: {
    [testType in DataTestType]: (frm: MainFormGroup) => (DataTest & { type: testType }) | null;
  } = {
    [DataTestType.Schema]: (mainForm: MainFormGroup): SchemaTestForm => {
      const result: SchemaTestForm = {
        type: DataTestType.Schema,
        allow_new_columns: mainForm[DataTestType.Schema].controls.allowNewColumns.value,
        schema: mainForm[DataTestType.Schema].controls.schema.controls.map((schemaFormGroup) => ({
          column: schemaFormGroup.controls.column.value,
          allow_null_values: schemaFormGroup.controls.allow_null_values.value,
          required: schemaFormGroup.controls.required.value,
          type: schemaFormGroup.controls.type.value,
        })),
      };
      return result;
    },
    [DataTestType.Unique]: (mainForm: MainFormGroup) => {
      const result: UniqueTestForm = {
        type: DataTestType.Unique,
        allow_null_values: mainForm[DataTestType.Unique].controls.allow_null_values.value,
        column: mainForm[DataTestType.Unique].controls.column.value,
      };
      return result;
    },
    [DataTestType.NumRowDrift]: (mainForm: MainFormGroup) => {
      const result: RowDriftTestForm = {
        type: DataTestType.NumRowDrift,
        row_change: {
          threshold_type: mainForm[DataTestType.NumRowDrift].controls.threshold_type.value,
          upper_threshold: mainForm[DataTestType.NumRowDrift].controls.upper_threshold.value,
          lower_threshold: mainForm[DataTestType.NumRowDrift].controls.lower_threshold.value,
        },
      };
      return result;
    },
    [DataTestType.ReferentialIntegrity]: (mainForm: MainFormGroup) => {
      const result: ReferentialIntegrityTestForm = {
        type: DataTestType.ReferentialIntegrity,
        column: mainForm[DataTestType.ReferentialIntegrity].controls.column.value,
        allow_null_values: mainForm[DataTestType.ReferentialIntegrity].controls.allow_null_values.value,
        foreign_table_path: mainForm[DataTestType.ReferentialIntegrity].controls.foreign_table_path.value,
        foreign_table_column: mainForm[DataTestType.ReferentialIntegrity].controls.foreign_table_column.value,
        rows_failed_by_condition: mainForm[DataTestType.ReferentialIntegrity].controls.rows_failed_by_condition.value,
        rows_failed_condition: mainForm[DataTestType.ReferentialIntegrity].controls.rows_failed_condition.value,
      };
      return result;
    },
    [DataTestType.Column]: (mainForm: MainFormGroup) => {
      const result: ColumnTestForm = {
        type: DataTestType.Column,
        column: mainForm[DataTestType.Column].controls.column.value,
        column_conditions: {
          operations: mainForm[DataTestType.Column].controls.operations.value,
          rows_failed_condition: mainForm[DataTestType.Column].controls.rows_failed_condition.value,
          rows_failed_by_condition: mainForm[DataTestType.Column].controls.rows_failed_by_condition.value,
        },
      };
      return result;
    },
    [DataTestType.LowCode]: (mainForm: MainFormGroup) => {
      const result: LowCodeTestForm = {
        type: DataTestType.LowCode,
        rows_failed_by_condition: mainForm[DataTestType.LowCode].controls.rows_failed_by_condition.value,
        rows_failed_condition: mainForm[DataTestType.LowCode].controls.rows_failed_condition.value,
        low_code_string: mainForm[DataTestType.LowCode].controls.low_code_string.value,
      };
      return result;
    },
  };

  private readonly patchFormFunctionByFormGroupKeyDef: Record<keyof MainFormGroup, (dataTest: DataTest) => void> = {
    dataTestType: (dataTest: DataTest) => this.patchDataTestType(dataTest.type),
    [DataTestType.Schema]: (dataTest: DataTest) => {
      if (dataTest.type !== DataTestType.Schema) {
        return;
      }
      this.patchSchemaForm({
        allowNewColumns: dataTest.allow_new_columns,
        schema: dataTest.schema,
      });
    },
    [DataTestType.NumRowDrift]: (dataTest: DataTest) => {
      if (dataTest.type !== DataTestType.NumRowDrift) {
        return;
      }
      this.patchNumRowDriftForm({
        upper_threshold: dataTest.row_change.upper_threshold,
        lower_threshold: dataTest.row_change.lower_threshold,
        threshold_type: dataTest.row_change.threshold_type,
      });
    },
    [DataTestType.Unique]: (dataTest: DataTest) => {
      if (dataTest.type !== DataTestType.Unique) {
        return;
      }
      this.patchUniqueForm({
        column: dataTest.column,
        allow_null_values: dataTest.allow_null_values,
      });
    },
    [DataTestType.ReferentialIntegrity]: (dataTest: DataTest) => {
      if (dataTest.type !== DataTestType.ReferentialIntegrity) {
        return;
      }
      this.patchReferentialIntegrityForm({
        rows_failed_by_condition: dataTest.rows_failed_by_condition,
        rows_failed_condition: dataTest.rows_failed_condition,
        allow_null_values: dataTest.allow_null_values,
        foreign_table_path: dataTest.foreign_table_path,
        column: dataTest.column,
        foreign_table_column: dataTest.foreign_table_column,
      });
    },
    [DataTestType.Column]: (dataTest: DataTest) => {
      if (dataTest.type !== DataTestType.Column) {
        return;
      }
      this.patchColumnTestForm({
        column: dataTest.column,
        rows_failed_condition:
          dataTest.column_conditions.rows_failed_condition ?? this.DEFAULT_VALUES.columnTestRowsFailedCondition,
        rows_failed_by_condition:
          dataTest.column_conditions.rows_failed_by_condition ?? this.DEFAULT_VALUES.columnTestRowsFailedByCondition,
        operations: dataTest.column_conditions.operations,
      });
    },
    [DataTestType.LowCode]: (dataTest: DataTest) => {
      if (dataTest.type !== DataTestType.LowCode) {
        return;
      }
      this.patchLowCodeForm({
        rows_failed_by_condition:
          dataTest.rows_failed_by_condition ?? this.DEFAULT_VALUES.lowCodeTestRowsFailedByCondition,
        rows_failed_condition: dataTest.rows_failed_condition ?? this.DEFAULT_VALUES.lowCodeTestRowsFailedCondition,
        low_code_string: dataTest.low_code_string ?? '',
      });
    },
  };

  readonly dataTestTypeFormControl = this.mainFormGroup.dataTestType;

  getFormByTestType<T extends DataTestType>(dataTestType: T): MainFormGroup[T] {
    return this.mainFormGroup[dataTestType];
  }

  getActiveFormGroup() {
    return this.mainFormGroup[this.mainFormGroup.dataTestType.value];
  }

  getDataTestToSave() {
    return this.dataTestToSaveFunctionByType[this.mainFormGroup.dataTestType.value](this.mainFormGroup);
  }

  addRowToSchemaTest(): void {
    const newItem = new FormGroup<SchemaTestSchemaFormGroup>({
      column: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      type: new FormControl<ColumnTypeEnum>(ColumnTypeEnum.Integer, { nonNullable: true }),
      allow_null_values: new FormControl<boolean>(true, { nonNullable: true }),
      required: new FormControl<boolean>(false, { nonNullable: true }),
    });
    newItem.controls.column.markAsTouched();
    this.mainFormGroup[DataTestType.Schema].controls.schema.push(newItem);
  }

  removeRowFromSchemaTest(index: number): void {
    const formGroupArray = this.mainFormGroup[DataTestType.Schema].controls.schema;
    if (formGroupArray.length <= index) {
      return;
    }
    formGroupArray.removeAt(index);
  }

  /**
   * Resets all form groups of the sidebar to their initial state
   */
  reset() {
    objectKeys(this.mainFormGroup).forEach((key) => {
      this.resetMainFormGroupByKey[key]();
    });
  }

  patchSchemaForm(data: { allowNewColumns: boolean; schema: SchemaTestForm['schema'] }): void {
    const schemaGroup = this.mainFormGroup[DataTestType.Schema];
    schemaGroup.controls.allowNewColumns.patchValue(data.allowNewColumns);
    // remove column names from schema which don't match with the input data to preserve form group state of existing data
    const formGroupsToRemove = schemaGroup.controls.schema.controls.filter(
      (formGroup) => !data.schema.map((s) => s.column).includes(formGroup.controls.column.value),
    );
    formGroupsToRemove.forEach((groupToRemove) => {
      const index = schemaGroup.controls.schema.controls.indexOf(groupToRemove);
      schemaGroup.controls.schema.removeAt(index);
    });

    // add new form groups for new column names
    const newSchemaItems = data.schema.filter(
      (schemaItem) =>
        !schemaGroup.controls.schema.controls
          .map((formGroup) => formGroup.controls.column.value)
          .includes(schemaItem.column),
    );

    for (const schemaItem of newSchemaItems) {
      const formGroup = new FormGroup<SchemaTestSchemaFormGroup>({
        required: new FormControl<boolean>(schemaItem.required ?? false, { nonNullable: true }),
        allow_null_values: new FormControl<boolean>(schemaItem.allow_null_values, { nonNullable: true }),
        column: new FormControl<string>(schemaItem.column, { nonNullable: true }),
        type: new FormControl<ColumnTypeEnum>(schemaItem.type, { nonNullable: true }),
      });
      schemaGroup.controls.schema.push(formGroup);
    }

    // sort columns by name
    const sortedGroups = [...schemaGroup.controls.schema.controls].sort((a, b) =>
      a.controls.column.value?.localeCompare(b.controls.column.value),
    );
    schemaGroup.controls.schema.clear();
    sortedGroups.forEach((formGroup) => schemaGroup.controls.schema.push(formGroup));
  }

  async fillMainFormByDataTest(dataTest: DataTest): Promise<void> {
    objectKeys(this.mainFormGroup).forEach((key) => this.patchFormFunctionByFormGroupKeyDef[key](dataTest));
  }

  patchDataTestType(data: DataTestType): void {
    this.mainFormGroup.dataTestType.patchValue(data);
  }

  patchNumRowDriftForm(data: Required<RowDriftTestForm['row_change']>): void {
    this.mainFormGroup[DataTestType.NumRowDrift].patchValue({
      lower_threshold: data.lower_threshold,
      upper_threshold: data.upper_threshold,
      threshold_type: data.threshold_type,
    });
  }

  patchUniqueForm(data: Required<Omit<UniqueTestForm, 'type'>>): void {
    this.mainFormGroup[DataTestType.Unique].patchValue({
      column: data.column,
      allow_null_values: data.allow_null_values,
    });
  }

  patchLowCodeForm(data: Required<Omit<LowCodeTestForm, 'type'>>): void {
    this.mainFormGroup[DataTestType.LowCode].patchValue({
      low_code_string: data.low_code_string,
      rows_failed_condition: data.rows_failed_condition,
      rows_failed_by_condition: data.rows_failed_by_condition,
    });
  }

  private createSchemaForm(): FormGroup<SchemaFormGroup> {
    return new FormGroup<SchemaFormGroup>({
      allowNewColumns: new FormControl<boolean>(false, { nonNullable: true }),
      schema: new FormArray<FormGroup<SchemaTestSchemaFormGroup>>([]),
    });
  }

  private createDataTestTypeFormControl(): FormControl<DataTestType> {
    return new FormControl<DataTestType>(DataTestType.Schema, { nonNullable: true });
  }

  private createNumRowDriftForm(): FormGroup<RowDriftTestFormGroup> {
    return new FormGroup<RowDriftTestFormGroup>({
      lower_threshold: new FormControl(0, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0)],
      }),
      upper_threshold: new FormControl(0, {
        nonNullable: true,
        validators: [Validators.required, Validators.min(0)],
      }),
      threshold_type: new FormControl('absolute', {
        nonNullable: true,
        validators: [Validators.required],
      }),
    });
  }

  private createUniqueForm(): FormGroup<UniqueFormGroup> {
    return new FormGroup<UniqueFormGroup>({
      allow_null_values: new FormControl<boolean>(true, { nonNullable: true }),
      column: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
    });
  }

  private createLowCodeTestForm(): FormGroup<LowCodeFormGroup> {
    return new FormGroup<LowCodeFormGroup>({
      low_code_string: new FormControl<string>('', { nonNullable: true }),
      rows_failed_condition: new FormControl<'absolute' | 'relative'>(
        this.DEFAULT_VALUES.lowCodeTestRowsFailedCondition,
        { nonNullable: true },
      ),
      rows_failed_by_condition: new FormControl<number>(this.DEFAULT_VALUES.lowCodeTestRowsFailedByCondition, {
        nonNullable: true,
      }),
    });
  }

  private createColumnTestForm(): FormGroup<ColumnTestFormGroup> {
    return new FormGroup<ColumnTestFormGroup>({
      column: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      operations: new FormControl<OperationModel[]>([], { nonNullable: true }),
      rows_failed_by_condition: new FormControl<number>(this.DEFAULT_VALUES.columnTestRowsFailedByCondition, {
        nonNullable: true,
      }),
      rows_failed_condition: new FormControl<'absolute' | 'relative'>(
        this.DEFAULT_VALUES.columnTestRowsFailedCondition,
        { nonNullable: true },
      ),
    });
  }

  private createReferentialIntegrityForm(): FormGroup<ReferentialIntegrityFormGroup> {
    return new FormGroup<ReferentialIntegrityFormGroup>({
      column: new FormControl<string>('', { nonNullable: true, validators: [Validators.required] }),
      foreign_table_path: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      foreign_table_column: new FormControl<string>('', {
        nonNullable: true,
        validators: [Validators.required],
      }),
      allow_null_values: new FormControl<boolean>(true, {
        nonNullable: true,
        validators: [Validators.required],
      }),
      rows_failed_condition: new FormControl<'absolute' | 'relative'>(
        this.DEFAULT_VALUES.referentialIntegrityTestRowsFailedCondition,
        {
          nonNullable: true,
        },
      ),
      rows_failed_by_condition: new FormControl<number>(
        this.DEFAULT_VALUES.referentialIntegrityTestRowsFailedByCondition,
        {
          nonNullable: true,
          validators: [Validators.required],
        },
      ),
    });
  }

  private patchReferentialIntegrityForm(data: Omit<ReferentialIntegrityTestForm, 'type'>): void {
    this.mainFormGroup[DataTestType.ReferentialIntegrity].patchValue({
      column: data.column,
      allow_null_values: data.allow_null_values,
      foreign_table_column: data.foreign_table_column,
      foreign_table_path: data.foreign_table_path,
      rows_failed_condition: data.rows_failed_condition,
      rows_failed_by_condition: data.rows_failed_by_condition,
    });
  }

  private patchColumnTestForm(data: ColumnTestFormObject): void {
    this.mainFormGroup[DataTestType.Column].patchValue({
      column: data.column,
      operations: data.operations,
      rows_failed_by_condition: data.rows_failed_by_condition,
      rows_failed_condition: data.rows_failed_condition,
    });
  }

  /**
   * Assign some complex validators to the main formGroup
   */
  private assignValidators(): void {
    const lowCodeControls = this.mainFormGroup[DataTestType.LowCode].controls;
    lowCodeControls.rows_failed_by_condition.addValidators([
      Validators.required,
      Validators.min(0),
      enableValidatorOn(integerValidator, () => lowCodeControls.rows_failed_condition.value === 'absolute'),
      enableValidatorOn(Validators.max(1), () => lowCodeControls.rows_failed_condition.value === 'relative'),
    ]);

    const columnControls = this.mainFormGroup[DataTestType.Column].controls;
    columnControls.rows_failed_by_condition.addValidators([
      Validators.required,
      Validators.min(0),
      enableValidatorOn(integerValidator, () => columnControls.rows_failed_condition.value === 'absolute'),
      enableValidatorOn(Validators.max(1), () => columnControls.rows_failed_condition.value === 'relative'),
    ]);

    const rowDriftControls = this.mainFormGroup[DataTestType.NumRowDrift].controls;
    rowDriftControls.upper_threshold.addValidators([
      enableValidatorOn(minNotIncludingValidator(1), () => rowDriftControls.threshold_type.value === 'relative'),
      enableValidatorOn(integerValidator, () => rowDriftControls.threshold_type.value === 'absolute'),
    ]);
    rowDriftControls.lower_threshold.addValidators([
      enableValidatorOn(minNotIncludingValidator(0), () => rowDriftControls.threshold_type.value === 'relative'),
      enableValidatorOn(integerValidator, () => rowDriftControls.threshold_type.value === 'absolute'),
      enableValidatorOn(Validators.max(1), () => rowDriftControls.threshold_type.value === 'relative'),
    ]);
  }
}

function objectKeys<T extends object>(arg: T): Array<keyof T> {
  if (isNullish(arg)) {
    return [];
  }
  return Object.keys(arg) as Array<keyof T>;
}

/**
 * enable or disable validation function on condition
 **/
function enableValidatorOn(
  validator: ValidatorFn,
  enable: () => boolean,
): (control: AbstractControl) => ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    const needValidation = enable();
    if (!needValidation) {
      return null;
    }
    return validator(control);
  };
}
